import { Injectable } from '@angular/core';
import {
  BasketAction,
  BASKET_CATEGORY,
} from '@epicuro-next/constants/platform/tracking/basket';
import { MessageType, showSnackBarMessage } from '@epicuro-next/state/snackbar';
import { gtmTrackActions } from '@epicuro-next/state/tracking';
import { isNonNullish } from '@epicuro-next/utilities';
import { downloadFile } from '@epicuro-next/utilities-browser';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { select, Store } from '@ngrx/store';
import { OrderType } from 'graphql-api/baskets-api-interfaces';
import { of } from 'rxjs';
import {
  catchError,
  concatMap,
  filter,
  map,
  switchMap,
  take,
  takeUntil,
  tap,
  withLatestFrom,
} from 'rxjs/operators';

import { CBasketService } from '../../services';
import { getCopyProductsBasketUpdate } from '../../shared';
import {
  clearBasket,
  clearBasketFailure,
  clearBasketManualDiscounts,
  clearBasketSuccess,
  clearBasketValidations,
  copyBasket,
  copyProductsToShops,
  downloadBasket,
  downloadBasketFailure,
  downloadBasketH2,
  downloadBasketPdfAsync,
  downloadBasketSuccess,
  loadBasketById,
  loadBasketFailure,
  loadBasketSuccess,
  loadDtoBasketFailure,
  loadDtoBasketSuccess,
  openCopyProductsModal,
  reuseBasket,
  reuseBasketFailure,
  reuseBasketSuccess,
  sendPdfConfirmation,
  sendPdfConfirmationFailed,
  sendPdfConfirmationSuccess,
  setBasketLoading,
  setBasketOperableStatus,
  switchBasketSuccess,
  triggerOpenCopyProductsModal,
  updateBasketItems,
  updateBasketItemsFailure,
} from '../actions';
import {
  selectBasketItems,
  selectBasketLineItemById,
  selectBasketStatus,
  selectCopyNowBasketEnabledRule,
  selectCopyProductsAllowedOrderTypesRule,
  selectCopyProductsRestrictedOrderType,
  selectDownloadH2Url,
  selectIsBasketFutureType,
  selectOpenedBasketId,
  selectOrderReferenceNumber,
} from '../selectors';

@Injectable()
export class BasketActionsEffects {
  constructor(
    private actions$: Actions,
    private cBasketService: CBasketService,
    private store: Store,
  ) {}

  public clearBasketAction$ = createEffect(() =>
    this.actions$.pipe(
      ofType(clearBasket),
      concatLatestFrom(() => [
        this.store.pipe(select(selectOpenedBasketId)),
        this.store.pipe(select(selectBasketStatus)),
      ]),
      switchMap(([, basketId, status]) => {
        return this.cBasketService.clearBasket(basketId, status).pipe(
          switchMap(() => [
            clearBasketManualDiscounts({ basketId }),
            clearBasketSuccess(),
            clearBasketValidations(),
            loadBasketById(),
            showSnackBarMessage({
              message: $localize`:basketActions|Basket cleared success message@@clearBasketSuccessMessage:Basket cleared successfully`,
              messageType: MessageType.SUCCESS,
            }),
          ]),
          catchError((error) => [
            clearBasketFailure({
              error: error.message,
            }),
            showSnackBarMessage({
              message: $localize`:basketActions|Basket cleared fail message@@clearBasketFailMessage:Failed to clear basket`,
              messageType: MessageType.DANGER,
            }),
          ]),
        );
      }),
    ),
  );

  public onBasketClearStart$ = createEffect(() =>
    this.actions$.pipe(
      ofType(clearBasket),
      switchMap(() => [
        setBasketLoading({ value: true }),
        setBasketOperableStatus({ isOperable: false }),
      ]),
    ),
  );

  public onBasketClearFinished$ = createEffect(() =>
    this.actions$.pipe(
      ofType(clearBasketSuccess, clearBasketFailure),
      switchMap(() => [
        setBasketLoading({ value: false }),
        setBasketOperableStatus({ isOperable: true }),
      ]),
    ),
  );

  public downloadBasket$ = createEffect(() =>
    this.actions$.pipe(
      ofType(downloadBasket),
      concatLatestFrom(() => this.store.pipe(select(selectOpenedBasketId))),
      switchMap(([_, basketId]) => {
        return this.cBasketService.exportBasket(basketId).pipe(
          switchMap(({ body, headers }) => {
            const contentDispositionHeader = headers.get('Content-Disposition');
            const filenameMatch = contentDispositionHeader
              ? contentDispositionHeader.match(/filename=("?)(.*)\1$/)
              : null;
            const filename = filenameMatch
              ? filenameMatch[2]
              : `basket_${basketId}.zip`;

            downloadFile(body as Blob, filename);

            return [
              downloadBasketSuccess(),
              showSnackBarMessage({
                message: $localize`:Basket|Basket download content success message@@downloadBasketContentSuccessMessage:Basket content downloaded`,
                messageType: MessageType.SUCCESS,
              }),
              gtmTrackActions.gtmTrackEvent({
                action: BasketAction.DOWNLOAD,
                properties: { category: BASKET_CATEGORY, label: basketId },
              }),
            ];
          }),
          catchError((error) => [
            downloadBasketFailure({ error: error.message }),
            showSnackBarMessage({
              message: $localize`:Basket|Basket download content failure message@@downloadBasketContentFailureMessage:Failed to download basket content`,
              messageType: MessageType.DANGER,
            }),
          ]),
        );
      }),
    ),
  );

  public downloadBasketH2$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(downloadBasketH2),
        concatLatestFrom(() =>
          this.store.pipe(select(selectDownloadH2Url), filter(isNonNullish)),
        ),
        tap(([, downloadH2Url]) => {
          window.open(downloadH2Url, '_blank');
        }),
      ),
    { dispatch: false },
  );

  public downloadBasketPdfAsync$ = createEffect(() =>
    this.actions$.pipe(
      ofType(downloadBasketPdfAsync),
      concatLatestFrom(() => this.store.pipe(select(selectOpenedBasketId))),
      switchMap(([_, basketId]) => {
        return this.cBasketService.downloadBasketPdfAsync(basketId).pipe(
          map(() => {
            return showSnackBarMessage({
              message: $localize`:Basket|Basket pdf download async success@@basketPdfDownloadAsyncSuccessMessage:An email with the link to download the PDF has been sent, please wait for some time for the email to arrive`,
              messageType: MessageType.SUCCESS,
            });
          }),
          catchError(() => {
            return of(
              showSnackBarMessage({
                message: $localize`:Basket|Basket pdf download async failure@@basketPdfDownloadAsyncFailureMessage:Failed to download basket PDF`,
                messageType: MessageType.DANGER,
              }),
            );
          }),
        );
      }),
    ),
  );

  public copyBasket$ = createEffect(() =>
    this.actions$.pipe(
      ofType(copyBasket),
      concatLatestFrom(() => [
        this.store.pipe(select(selectIsBasketFutureType)),
        this.store.pipe(select(selectOpenedBasketId)),
        this.store.pipe(select(selectCopyNowBasketEnabledRule)),
      ]),
      map(([_, isBasketFutureType, basketId, isCopyNowEnabled]) => {
        if (isBasketFutureType || isCopyNowEnabled) {
          return reuseBasket({ basketId });
        }

        return showSnackBarMessage({
          message: $localize`:Basket|Failed to copy Now products basket message@@BasketCopyNowProductsFailure:Failed to copy basket: Now Products in the Basket`,
          messageType: MessageType.DANGER,
        });
      }),
    ),
  );

  public reuseBasket$ = createEffect(() =>
    this.actions$.pipe(
      ofType(reuseBasket),
      switchMap(({ basketId }) =>
        this.cBasketService.reuseBasket(basketId).pipe(
          switchMap((basket) => [
            loadDtoBasketSuccess({ basket }),
            reuseBasketSuccess({ basketId: basket.basketId }),
          ]),
          catchError((error) => [
            reuseBasketFailure({ error: error.message }),
            showSnackBarMessage({
              message: $localize`:Basket|Failed to reuse basket message@@BasketReuseFailure:Failed to reuse basket.`,
              messageType: MessageType.DANGER,
            }),
          ]),
        ),
      ),
    ),
  );

  public reuseBasketSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(reuseBasketSuccess),
      concatLatestFrom(() =>
        this.store.pipe(select(selectOrderReferenceNumber)),
      ),
      switchMap(([{ basketId }, refNumber]) => [
        showSnackBarMessage({
          message: $localize`:Basket|Success to reuse basket message@@BasketReuseSuccess:This is the copied basket from basket ${refNumber}`,
          messageType: MessageType.SUCCESS,
          hideDelay: 5000,
        }),
        switchBasketSuccess({ basketId }),
      ]),
    ),
  );

  public triggerOpenCopyProductsModal$ = createEffect(() =>
    this.actions$.pipe(
      ofType(triggerOpenCopyProductsModal),
      concatMap(({ uniqueAddressLineId }) =>
        of(uniqueAddressLineId).pipe(
          withLatestFrom(
            this.store.pipe(select(selectCopyProductsRestrictedOrderType)),
            this.store.pipe(
              select(selectBasketLineItemById(uniqueAddressLineId)),
            ),
          ),
        ),
      ),
      map(([uniqueAddressLineId, unAllowedOrderType, basketLineItem]) => {
        if (
          unAllowedOrderType &&
          basketLineItem.basketItems.every(
            ({ orderType }) => unAllowedOrderType === orderType,
          )
        ) {
          const messages = {
            [OrderType.Now]: $localize`:Basket|Copy products now restriction snackbar message@@CopyProductsNowRestrictionSnackBar:Copying now items is not allowed`,
            [OrderType.Future]: $localize`:Basket|Copy products preorder restriction snackbar message@@CopyProductsFutureRestrictionSnackBar:Copying future items is not allowed`,
          };
          return showSnackBarMessage({
            messageType: MessageType.WARNING,
            message: messages[unAllowedOrderType as keyof typeof messages],
          });
        }

        return openCopyProductsModal({
          uniqueAddressLineId,
          options: {
            scrollable: true,
          },
        });
      }),
    ),
  );

  public copyProductsToShops$ = createEffect(() =>
    this.actions$.pipe(
      ofType(copyProductsToShops),
      concatMap((action) =>
        of(action).pipe(
          withLatestFrom(
            this.store.pipe(select(selectBasketItems)),
            this.store.pipe(select(selectCopyProductsAllowedOrderTypesRule)),
          ),
        ),
      ),
      map(([{ toShops, fromShop }, basketItems, allowedOrderTypes]) => {
        const filteredBasketItems = basketItems.filter(({ orderType }) =>
          allowedOrderTypes.includes(orderType),
        );
        return { toShops, fromShop, basketItems: filteredBasketItems };
      }),
      map(({ toShops, fromShop, basketItems }) =>
        getCopyProductsBasketUpdate(basketItems, fromShop, toShops),
      ),
      map((updateShops) => updateBasketItems(updateShops)),
    ),
  );

  public showSnackBarAfterCopySuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(copyProductsToShops),
      switchMap(() =>
        this.actions$.pipe(
          ofType(loadBasketSuccess, loadDtoBasketSuccess),
          take(1),
          takeUntil(
            this.actions$.pipe(
              ofType(
                updateBasketItemsFailure,
                loadBasketFailure,
                loadDtoBasketFailure,
              ),
            ),
          ),
        ),
      ),
      map(() =>
        showSnackBarMessage({
          messageType: MessageType.INFO,
          message: $localize`:Basket|Copy products success snackbar message@@CopyProductsSuccessSnackBar:Inventories can be limited. Please validate your basket to check the availability of copied items`,
          hideDelay: 8000,
        }),
      ),
    ),
  );

  public sendPdfConfirmation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(sendPdfConfirmation),
      concatLatestFrom(() => this.store.pipe(select(selectOpenedBasketId))),
      switchMap(([_, basketId]) =>
        this.cBasketService.sendPdfConfirmation(basketId).pipe(
          map(() => sendPdfConfirmationSuccess()),
          catchError((error) =>
            of(sendPdfConfirmationFailed({ message: error.message })),
          ),
        ),
      ),
    ),
  );

  public showSnackbarAfterSendPdfConfirmationSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(sendPdfConfirmationSuccess),
      map(() =>
        showSnackBarMessage({
          messageType: MessageType.SUCCESS,
          message: $localize`:Basket|Send pdf confirmation success snackbar message@@SendPdfConfirmationSuccessSnackBar:Successfully Sent PDF Confirmation`,
          hideDelay: 5000,
        }),
      ),
    ),
  );

  public showSnackbarAfterSendPdfConfirmationFailed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(sendPdfConfirmationFailed),
      map(({ message }) =>
        showSnackBarMessage({
          messageType: MessageType.DANGER,
          message: $localize`:Basket|Send pdf confirmation failed snackbar message@@SendPdfConfirmationFailedSnackBar:${message}`,
          hideDelay: 5000,
        }),
      ),
    ),
  );
}
